import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { type ExportFormat } from '@/components/atoms/ExportButton/ExportButton';
import { cn } from '@/lib/utils';
import {
  DistributionBarCard,
  type DistributionBarItem,
} from '@/modules/analytics/components/DistributionBarCard/DistributionBarCard';
import { buildExportFilename } from '@/modules/analytics/utils/exportFilename';
import { downloadCsv, downloadJson } from '@/util/fileManager';

import type { CategoricalInsight } from './hooks/useSingleAgentCustomInsights';

const BAR_COLOR_CLASSES = [
  'bg-chart-1',
  'bg-chart-2',
  'bg-chart-3',
  'bg-chart-4',
  'bg-chart-5',
] as const;

type CategoricalInsightBarCardProps = {
  dateRangeLabel: string;
  insight: CategoricalInsight;
  className?: string;
};

/**
 * Vertical bar distribution for a single categorical custom insight (Figma: categorical).
 * Bar geometry matches design: 44px-wide bars centered within 80px columns, 165px chart
 * track height, columns clustered (`justify-center`) so a small number of bars don't feel
 * spread apart. Labels are capped at 80px and truncate after 2 lines.
 */
export const CategoricalInsightBarCard = ({
  dateRangeLabel,
  insight,
  className,
}: CategoricalInsightBarCardProps) => {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);

  const items = useMemo<DistributionBarItem[]>(
    () =>
      insight.buckets.map((row, i) => ({
        id: row.id,
        label: row.label,
        value: row.value,
        barClassName: BAR_COLOR_CLASSES[i % BAR_COLOR_CLASSES.length],
        extraRow: { label: t('analytics.users'), value: row.count.toLocaleString() },
      })),
    [insight.buckets, t]
  );

  const handleExport = useCallback(
    (exportFormat: ExportFormat) => {
      try {
        setIsExporting(true);
        const filename = buildExportFilename(`categorical_insight_${insight.insightName}`);
        const rows = items.map(({ id, label, value }) => ({ id, label, percentage: value }));

        if (exportFormat === 'json') {
          downloadJson({ categorical_insight: rows }, filename);
        } else {
          downloadCsv(rows, filename);
        }
      } catch {
        toast.error(t('analytics.export_error'));
      } finally {
        setIsExporting(false);
      }
    },
    [items, insight.insightName, t]
  );

  return (
    <DistributionBarCard
      className={cn('min-h-60', className)}
      title={insight.insightName}
      description={dateRangeLabel}
      items={items}
      exportConfig={{ onExport: handleExport, isExporting }}
      maxBarHeightPx={165}
      showValueAboveBar
      barsRowClassName="flex w-full min-w-0 items-end justify-center gap-6"
      barColumnClassName="flex w-20 shrink-0 flex-col items-center"
      barButtonClassName="w-11"
      barLabelClassName="line-clamp-2 w-20 text-center text-xs leading-normal break-words text-muted-foreground"
      chartLabelsGapClassName="gap-2"
      barRadiusClassName="rounded-md"
    />
  );
};
